import React, { useState, useEffect } from "react";
import { createProduct, updateProduct, getSuppliers } from "../../services/apiService";
import ProductImageManager from "../../components/ProductImageManager";
import { trackCustom } from "../../utils/analytics";
import { FaSave, FaEdit, FaBarcode, FaTag, FaBox, FaStore, FaLink } from "react-icons/fa";

// Fuera del componente para que tenga una referencia estable entre renders
// (si viviera dentro, cada render crearía un objeto nuevo y no podríamos
// declararlo como dependencia del useEffect de abajo sin dispararlo siempre).
const initialState = {
  nombre: "",
  descripcion: "",
  sku: "",
  codigoBarras: "",
  precioCompra: "",
  precioVenta: "",
  stockActual: "",
  unidadMedida: "",
  categoria: "",
  imagenUrl: "",
  supplierId: "",
  visibleEnCatalogo: false,
  esDigital: false,
  linkCompra: "",
};

const ProductoForm = ({ onProductCreated, productToEdit, onEditComplete, onCancelEdit }) => {
  const [formData, setFormData] = useState(initialState);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        // (1, 1000) para traer todos los proveedores del combo, no solo la
        // primera página de 10 que trae el backend paginado.
        const data = await getSuppliers(1, 1000);
        setSuppliers(data.suppliers || []);
      } catch (err) {
        setError("No se pudieron cargar los proveedores.");
      } finally {
        setLoadingSuppliers(false);
      }
    };
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (productToEdit) {
      setFormData({
        ...productToEdit,
        precioCompra: productToEdit.precioCompra?.toString() || "",
        precioVenta: productToEdit.precioVenta?.toString() || "",
        stockActual: productToEdit.stockActual?.toString() || "",
        supplierId: productToEdit.supplierId?.toString() || "",
        esDigital: Boolean(productToEdit.esDigital),
        linkCompra: productToEdit.linkCompra || "",
        codigoBarras: productToEdit.codigoBarras || "",
      });
    } else {
      setFormData(initialState);
    }
    setMessage("");
    setError("");
  }, [productToEdit]); // eslint-disable-line react-hooks/exhaustive-deps -- initialState es un objeto constante fuera del componente, no cambia entre renders

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const digital = Boolean(formData.esDigital);
    const dataToSend = {
      ...formData,
      // Un producto digital no lleva stock ni necesita costo ni unidad: se envían valores neutros.
      precioCompra: digital ? Number(formData.precioCompra || 0) : Number(formData.precioCompra),
      precioVenta: Number(formData.precioVenta),
      stockActual: digital ? 0 : Number(formData.stockActual),
      unidadMedida: digital ? (formData.unidadMedida || "Digital") : formData.unidadMedida,
      supplierId: formData.supplierId ? Number(formData.supplierId) : null,
      visibleEnCatalogo: Boolean(formData.visibleEnCatalogo),
      esDigital: digital,
      linkCompra: digital ? (formData.linkCompra || "").trim() : null,
      codigoBarras: (formData.codigoBarras || "").trim(),
    };

    try {
      if (productToEdit) {
        await updateProduct(productToEdit.id, dataToSend);
        setMessage("✅ Producto actualizado con éxito");
        setTimeout(() => onEditComplete(), 1500);
      } else {
        await createProduct(dataToSend);
        trackCustom('ProductoCreado');
        setMessage("🚀 Producto creado con éxito");
        setFormData(initialState);
        onProductCreated();
      }
    } catch (err) {
      setError(err.message || "Error en la operación.");
    }
  };

  if (loadingSuppliers) return <div className="p-10 text-center animate-pulse text-blue-600 font-medium">Cargando proveedores...</div>;

  return (
    <div className="max-w-4xl mx-auto bg-gray-50 rounded-2xl shadow-xl overflow-hidden border border-gray-100">
      {/* Header Dinámico */}
      <div className={`p-6 text-white flex items-center gap-3 ${productToEdit ? 'bg-amber-500' : 'bg-blue-600'}`}>
        {productToEdit ? <FaEdit size={24} /> : <FaSave size={24} />}
        <h2 className="text-2xl font-bold">
          {productToEdit ? "Modificar Producto" : "Registrar Nuevo Producto"}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8">
        
        {/* Sección 1: Identificación */}
        <section>
          <div className="flex items-center gap-2 mb-4 text-blue-700 border-b pb-2">
            <FaTag /> <h3 className="font-bold uppercase tracking-wider text-sm">Información General</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase">Nombre del Producto *</label>
              <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} required
                className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all outline-none" 
                placeholder="Ej: Camiseta Algodón XL" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase">SKU / Referencia *</label>
              <input type="text" name="sku" value={formData.sku} onChange={handleChange} required
                className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all outline-none" 
                placeholder="PROD-001" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label htmlFor="codigoBarras" className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                <FaBarcode /> Código de barras (opcional)
              </label>
              <input id="codigoBarras" type="text" name="codigoBarras" value={formData.codigoBarras} onChange={handleChange}
                // El lector termina cada código con Enter: aquí no debe enviar el formulario.
                onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                autoComplete="off"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all outline-none font-mono"
                placeholder="Escanéalo con el lector o escríbelo (ej: 7702004003508)" />
              <p className="text-[11px] text-gray-400">Si el producto no trae código, déjalo vacío. En Vender (POS) basta con escanearlo para agregarlo al carrito.</p>
            </div>
          </div>
        </section>

        {/* Producto digital (ej. Hotmart) */}
        <section>
          <div className="flex items-center gap-2 mb-4 text-purple-700 border-b pb-2">
            <FaLink /> <h3 className="font-bold uppercase tracking-wider text-sm">Producto digital</h3>
          </div>
          <label className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 cursor-pointer">
            <input
              type="checkbox"
              name="esDigital"
              checked={formData.esDigital}
              onChange={handleChange}
              className="w-5 h-5 accent-purple-600"
            />
            <div>
              <p className="font-bold text-gray-700 text-sm">Este producto se compra en un link externo (ej. Hotmart)</p>
              <p className="text-xs text-gray-400">No lleva stock. En el catálogo tu cliente deja sus datos y es llevado a pagar; tú verificas el pago y confirmas la venta desde Pedidos.</p>
            </div>
          </label>
          {formData.esDigital && (
            <div className="space-y-1 mt-4">
              <label className="text-xs font-bold text-gray-500 uppercase">Link de compra (https) *</label>
              <input type="url" name="linkCompra" value={formData.linkCompra} onChange={handleChange} required
                className="w-full p-3 bg-white border border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 transition-all outline-none"
                placeholder="https://pay.hotmart.com/..." />
            </div>
          )}
        </section>

        {/* Sección 2: Precios e Inventario */}
        <section>
          <div className="flex items-center gap-2 mb-4 text-green-700 border-b pb-2">
            <FaBox /> <h3 className="font-bold uppercase tracking-wider text-sm">Precios e Inventario</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase">Precio Compra ($) *</label>
              <input type="number" name="precioCompra" value={formData.precioCompra} onChange={handleChange} required={!formData.esDigital} step="0.01"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 transition-all outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase">Precio Venta ($) *</label>
              <input type="number" name="precioVenta" value={formData.precioVenta} onChange={handleChange} required step="0.01"
                className="w-full p-3 bg-green-50 border border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 transition-all outline-none font-bold text-green-700" />
            </div>
            {!formData.esDigital && (
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">Stock Inicial *</label>
                <input type="number" name="stockActual" value={formData.stockActual} onChange={handleChange} required min="0"
                  className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all outline-none" />
              </div>
            )}
          </div>
        </section>

        {/* Sección 3: Categorización */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Unidad (Kg, Unid, Par)</label>
            <input type="text" name="unidadMedida" value={formData.unidadMedida} onChange={handleChange} required={!formData.esDigital}
              className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ej: Unid" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Categoría</label>
            <input type="text" name="categoria" value={formData.categoria} onChange={handleChange} required
              className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ej: Ropa" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Proveedor</label>
            <select name="supplierId" value={formData.supplierId} onChange={handleChange}
              className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Ninguno</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
        </div>

        {/* Sección 4: Catálogo público */}
        <section>
          <div className="flex items-center gap-2 mb-4 text-purple-700 border-b pb-2">
            <FaStore /> <h3 className="font-bold uppercase tracking-wider text-sm">Catálogo público</h3>
          </div>
          <label className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 cursor-pointer">
            <input
              type="checkbox"
              name="visibleEnCatalogo"
              checked={formData.visibleEnCatalogo}
              onChange={handleChange}
              className="w-5 h-5 accent-purple-600"
            />
            <div>
              <p className="font-bold text-gray-700 text-sm">Mostrar este producto en el catálogo público</p>
              <p className="text-xs text-gray-400">Los clientes podrán verlo y pedirlo desde la vitrina en línea de tu negocio.</p>
            </div>
          </label>

          {productToEdit ? (
            <div className="mt-6">
              <ProductImageManager productId={productToEdit.id} initialImages={productToEdit.images || []} />
            </div>
          ) : (
            <p className="text-xs text-gray-400 mt-3 italic">Guarda el producto primero para poder agregarle fotos.</p>
          )}
        </section>

        {/* Botón de Acción */}
        <div className="pt-6">
          <button type="submit"
            className={`w-full py-4 rounded-xl text-lg font-bold shadow-lg transform transition active:scale-95 flex items-center justify-center gap-2 ${
              productToEdit ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}>
            {productToEdit ? <><FaEdit /> Actualizar Producto</> : <><FaSave /> Guardar Producto</>}
          </button>
          {productToEdit && onCancelEdit && (
            <button type="button" onClick={onCancelEdit}
              className="w-full mt-3 py-3 rounded-xl text-sm font-bold bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 transition">
              Cancelar edición y crear un producto nuevo
            </button>
          )}
        </div>

        {/* Mensajes de Feedback */}
        {message && <div className="p-4 bg-green-100 text-green-700 rounded-xl text-center font-bold animate-bounce">{message}</div>}
        {error && <div className="p-4 bg-red-100 text-red-700 rounded-xl text-center font-bold">{error}</div>}
      </form>
    </div>
  );
};

export default ProductoForm;